import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Minus, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingButton } from '@/components/LoadingButton';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { useError } from '@/contexts/ErrorContext';
import {
  useGetOrgProductPlans,
  useCreateOrgCheckoutSession,
  getGetOrgProductPlansQueryKey,
} from '@/api/auth/billing/billing';
import type { PlanResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { formatMinorUnitsAmount } from '@/lib/formatCurrency';

/**
 * Props for PlanPickerDialog
 */
interface PlanPickerDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** The product to subscribe to */
  productId: string;
}

/**
 * The minimum purchasable quantity for a plan. The server may leave the bound
 * unset (0) on non-adjustable plans, but a purchase is always at least 1.
 */
function getMinQuantity(plan: PlanResponse): number {
  return Math.max(plan.minQuantity, 1);
}

/**
 * Dialog that renders the plans the organization is eligible to purchase, as resolved
 * server-side by the Auth Service, and opens Stripe Checkout for the chosen plan.
 * The opaque planId is echoed back verbatim at checkout — never parsed or constructed.
 */
export function PlanPickerDialog({ open, onOpenChange, productId }: PlanPickerDialogProps) {
  const { t, language } = useLanguage();
  const { handleApiError } = useError();
  const { selectedOrganizationId } = useOrgs();
  const queryClient = useQueryClient();

  const [selectedPlanId, setSelectedPlanId] = useState<string>();
  const [quantity, setQuantity] = useState(1);

  // Request options with Accept-Language header for localized responses
  const requestOptions = { headers: { 'Accept-Language': language } };

  const {
    data: plansResponse,
    isLoading,
    error,
    refetch,
  } = useGetOrgProductPlans(selectedOrganizationId ?? '', productId, {
    query: {
      enabled: !!selectedOrganizationId && open,
    },
    request: requestOptions,
  });

  const plans = useMemo(() => plansResponse?.data.plans ?? [], [plansResponse]);

  const selectedPlan = plans.find((plan) => plan.planId === selectedPlanId);

  // The quantity that will actually be purchased for the selected plan.
  const purchaseQuantity =
    selectedPlan && !selectedPlan.adjustableQuantity ? getMinQuantity(selectedPlan) : quantity;

  const checkoutMutation = useCreateOrgCheckoutSession({ request: requestOptions });

  // Keep the selection valid: pre-select the server-marked default plan, and re-select
  // when a re-fetch (e.g. after a checkout conflict) drops the previously chosen plan.
  useEffect(() => {
    if (!open || plans.length === 0) return;
    if (plans.some((plan) => plan.planId === selectedPlanId)) return;

    const initialPlan = plans.find((plan) => plan.isDefault) ?? plans[0];
    setSelectedPlanId(initialPlan.planId);
    setQuantity(getMinQuantity(initialPlan));
  }, [open, plans, selectedPlanId]);

  /**
   * Format a plan amount using the plan's currency (falls back to the response currency)
   */
  const formatPlanAmount = useCallback(
    (amount: number, plan: PlanResponse) => {
      const currency = plan.currency ?? plansResponse?.data.currency ?? 'USD';
      return formatMinorUnitsAmount(amount, currency, language);
    },
    [plansResponse, language]
  );

  /**
   * Get the interval suffix for display
   */
  const getIntervalSuffix = useCallback(
    (interval: string) => {
      if (interval === 'year') return t.billing.planPicker.perYear;
      return t.billing.planPicker.perMonth;
    },
    [t]
  );

  /**
   * Handle selecting a plan — quantity restarts at the plan's minimum
   */
  const handleSelectPlan = useCallback(
    (planId: string) => {
      setSelectedPlanId(planId);
      const plan = plans.find((p) => p.planId === planId);
      setQuantity(plan ? getMinQuantity(plan) : 1);
    },
    [plans]
  );

  /**
   * Handle quantity stepper changes, clamped to the plan's bounds
   */
  const handleQuantityChange = useCallback(
    (delta: number) => {
      if (!selectedPlan) return;
      setQuantity((current) => {
        const next = current + delta;
        if (next < getMinQuantity(selectedPlan)) return getMinQuantity(selectedPlan);
        if (selectedPlan.maxQuantity != null && next > selectedPlan.maxQuantity) {
          return selectedPlan.maxQuantity;
        }
        return next;
      });
    },
    [selectedPlan]
  );

  /**
   * Create the checkout session for the chosen plan and redirect to Stripe
   */
  const handleSubscribe = useCallback(async () => {
    if (!selectedOrganizationId || !selectedPlan) return;

    try {
      const response = await checkoutMutation.mutateAsync({
        orgId: selectedOrganizationId,
        data: {
          productId,
          // Echo the opaque planId back verbatim — do NOT parse or build it.
          planId: selectedPlan.planId,
          quantity: purchaseQuantity,
          successUrl: window.location.href,
          cancelUrl: window.location.href,
          locale: language,
        },
      });

      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error) {
      handleApiError(error, { context: 'PlanPickerDialog.checkout' }, { showToast: false });
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 400 || status === 409) {
        // The chosen plan is not (or no longer) valid for this org — re-fetch the eligible plans.
        toast.error(t.billing.planPicker.planUnavailable);
        await queryClient.invalidateQueries({
          queryKey: getGetOrgProductPlansQueryKey(selectedOrganizationId, productId),
        });
      } else {
        toast.error(t.billing.planPicker.checkoutError);
      }
    }
  }, [
    selectedOrganizationId,
    selectedPlan,
    checkoutMutation,
    productId,
    purchaseQuantity,
    language,
    handleApiError,
    queryClient,
    t,
  ]);

  /**
   * Render loading skeleton while plans are fetched
   */
  const renderSkeleton = () => (
    <div className="space-y-2">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );

  /**
   * Render a single selectable plan card
   */
  const renderPlan = (plan: PlanResponse) => {
    const savingsPercent =
      plan.savingsVsMonthlyBps != null ? Math.round(plan.savingsVsMonthlyBps / 100) : null;

    return (
      <Label
        key={plan.planId}
        htmlFor={plan.planId}
        className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-muted/50"
      >
        <div className="flex items-center gap-3">
          <RadioGroupItem id={plan.planId} value={plan.planId} />
          <span className="flex items-center gap-2 font-medium">
            {plan.displayName}
            {savingsPercent != null && savingsPercent > 0 && (
              <Badge variant="secondary">
                {t.billing.planPicker.savePercent.replace('{percent}', String(savingsPercent))}
              </Badge>
            )}
          </span>
        </div>
        {plan.unitAmount != null && (
          <span className="text-sm text-muted-foreground">
            {formatPlanAmount(plan.unitAmount, plan)}
            {getIntervalSuffix(plan.interval)}
          </span>
        )}
      </Label>
    );
  };

  /**
   * Render the plan list with quantity selection and total
   */
  const renderContent = () => {
    if (plans.length === 0) {
      return (
        <p className="py-4 text-center text-sm text-muted-foreground">
          {t.billing.planPicker.noPlans}
        </p>
      );
    }

    return (
      <div className="space-y-4">
        <RadioGroup value={selectedPlanId ?? ''} onValueChange={handleSelectPlan}>
          {plans.map(renderPlan)}
        </RadioGroup>

        {selectedPlan?.adjustableQuantity && (
          <div className="flex items-center justify-between rounded-lg border p-3">
            <span className="text-sm font-medium">{t.billing.planPicker.quantity}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  handleQuantityChange(-1);
                }}
                disabled={quantity <= getMinQuantity(selectedPlan)}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center font-medium">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  handleQuantityChange(1);
                }}
                disabled={selectedPlan.maxQuantity != null && quantity >= selectedPlan.maxQuantity}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {selectedPlan?.unitAmount != null && (
          <div className="flex justify-between rounded-lg bg-muted/50 p-4">
            <span className="text-muted-foreground">{t.billing.planPicker.total}</span>
            <span className="font-semibold">
              {formatPlanAmount(selectedPlan.unitAmount * purchaseQuantity, selectedPlan)}
              {getIntervalSuffix(selectedPlan.interval)}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t.billing.planPicker.title}</DialogTitle>
          <DialogDescription>{t.billing.planPicker.description}</DialogDescription>
        </DialogHeader>

        {error ? (
          <QueryErrorState
            error={error}
            message={t.billing.planPicker.failedToLoad}
            onRetry={() => void refetch()}
          />
        ) : isLoading ? (
          renderSkeleton()
        ) : (
          renderContent()
        )}

        <DialogFooter className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={checkoutMutation.isPending}
          >
            {t.wizard.common.cancel}
          </Button>
          <LoadingButton
            onClick={() => void handleSubscribe()}
            disabled={!selectedPlan || checkoutMutation.isPending}
            isLoading={checkoutMutation.isPending}
          >
            {t.billing.subscribe}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
